import { Icon } from '@drdropin-tech/theseus';
import { fetchNextAvailable } from '@packages/cortex-sdk';
import { localesNextAvailableWithTime, timeZone } from '@packages/shared';
import { Locales } from '@packages/types';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { formatRelative, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useEffect } from 'react';

type AvailabilityDecoratorProps = {
  clinicId: string;
  serviceId: string;
  practitionerId?: string;
  index: number;
  locale: Locales;
  minimize?: boolean;
  addToNextAvailableArray: (clinicId: string, time: number) => void;
};

export default function NextAvailable({
  clinicId,
  serviceId,
  practitionerId,
  index,
  locale = 'nb',
  minimize = false,
  addToNextAvailableArray,
}: AvailabilityDecoratorProps) {
  const queryKey = practitionerId
    ? ['next-available', clinicId, serviceId, practitionerId]
    : ['next-available', clinicId, serviceId];
  const { data: nextAvailableData, isLoading } = useQuery(
    queryKey,
    ({ signal }) =>
      fetchNextAvailable({
        clinicId,
        serviceId,
        practitionerId,
        signal,
      }),
    {
      enabled: !!serviceId,
      select: (nextAvailableSlot) => {
        if (nextAvailableSlot) {
          const zonedDate = utcToZonedTime(
            parseISO(nextAvailableSlot.startTime),
            timeZone,
          );
          return {
            label: formatRelative(zonedDate, new Date(), {
              locale: localesNextAvailableWithTime[locale],
            }),
            time: zonedDate.getTime(),
          };
        }
      },
    },
  );

  useEffect(() => {
    if (nextAvailableData) {
      addToNextAvailableArray(clinicId, nextAvailableData.time);
    }
  }, [nextAvailableData, clinicId, addToNextAvailableArray]);

  if (!nextAvailableData && !isLoading) return null;

  if (isLoading) {
    return <LoadingNextAvailable index={index} />;
  }

  return (
    <NextAvailablePill label={nextAvailableData?.label} minimize={minimize} />
  );
}

export function NextAvailablePill({
  label,
  minimize = false,
}: {
  label?: string;
  minimize?: boolean;
}) {
  return (
    <div
      data-testid="next-available-pill"
      className={classNames(
        'border-accent bg-accent group-hover:border-primary mt-2 flex items-center gap-2 rounded-full border px-3 py-1',
        minimize ? 'mt-0 py-0 text-[14px]' : 'mt-2 py-1',
      )}
    >
      {!minimize && <Icon name="Clock" iconSize="xs" />}
      <span className="flex items-center gap-2 font-normal">{label}</span>
    </div>
  );
}

export function LoadingNextAvailable({
  index,
  minimize = false,
}: {
  index: number;
  minimize?: boolean;
}) {
  return (
    <span
      className={classNames(
        'border-accent skeleton-available w-30 group-hover:border-primary flex items-center gap-2 rounded-full border px-3',
        minimize ? 'mt-0 h-[22px] py-0' : 'mt-2 h-7 py-1',
      )}
      style={{
        animationDelay: `${index * 0.1}s`,
      }}
    />
  );
}
